import React, { useState } from 'react';
import { useTheme } from '@mui/material';
import DSBox from '../DSBox';
import DSTypography from '../Typography/DSTypography';
import DSCalendarEvent from './DSCalendarEvent';
import { CalendarDay, isPastDate } from './utils/calendarUtils';
import {
  KLYDO_YELLOW,
  KLYDO_BLACK,
  KLYDO_WHITE,
  NATIONAL_HOLIDAY_BACKGROUND_COLOR,
  CALENDAR_NOT_IN_MONTH_DAY_COLOR,
} from '../../consts/colors';
import { isNationalHoliday } from '../../utils/calendarEvents';
import DSButton from '../DSButton';
import CalendarDayDialog from '../../views/Calendar/components/CalendarDayDialog';
import { CalendarEvent } from '../../api/calendarEvent';

type DSCalendarDayProps = {
  day: CalendarDay;
  onClick: (calendarEventId?: string) => void;
};

const MAX_VISIBLE_EVENTS = 2;

const DSCalendarDay = ({ day, onClick }: DSCalendarDayProps) => {
  const { spacing, palette, breakpoints } = useTheme();
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const visibleEvents = showAllEvents
    ? day.events
    : day.events.slice(0, MAX_VISIBLE_EVENTS);
  const hiddenEventsCount = day.events.length - MAX_VISIBLE_EVENTS;

  const handleShowMore = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowAllEvents(!showAllEvents);
  };

  const handleDayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (day.isCurrentMonth) {
      setDialogOpen(true);
    }
  };

  const handleCalendarEventClick = (event: CalendarEvent) => {
    if (isPastDate(day.date)) return;
    onClick(event.id);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const isClickDisabled = isPastDate(day.date);

  const isNationalCalendarEventType = day.events.some((event) =>
    isNationalHoliday(event),
  );

  const backgroundColor = () => {
    if (isNationalCalendarEventType && day.isCurrentMonth)
      return NATIONAL_HOLIDAY_BACKGROUND_COLOR;
    if (!isNationalCalendarEventType && day.isCurrentMonth)
      return palette.paper.elevation3;
    return CALENDAR_NOT_IN_MONTH_DAY_COLOR;
  };

  return (
    <>
      <DSBox
        onClick={handleDayClick}
        sx={{
          backgroundColor: backgroundColor(),
          borderRadius: spacing(1.5),
          padding: spacing(1),
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          boxSizing: 'border-box',
          cursor: day.isCurrentMonth ? 'pointer' : 'default',
          '&:hover': {
            opacity: day.isCurrentMonth ? 0.9 : 1,
          },
          [breakpoints.down('md')]: {
            padding: spacing(0.5),
            borderRadius: spacing(1),
          },
        }}
      >
        <DSBox
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: spacing(1),
            minHeight: spacing(4),
            position: 'relative',
          }}
        >
          <DSBox
            sx={{
              backgroundColor: day.isToday ? KLYDO_YELLOW : 'transparent',
              color: day.isToday ? KLYDO_BLACK : KLYDO_WHITE,
              borderRadius: '50%',
              width: spacing(4),
              height: spacing(4),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: 'auto',
            }}
          >
            <DSTypography
              sx={{
                fontFamily: 'Gilroy-Medium',
                fontSize: '0.875rem',
                color: 'inherit',
                opacity: day.isCurrentMonth ? 1 : 0.5,
              }}
            >
              {day.dayNumber.toString().padStart(2, '0')}
            </DSTypography>
          </DSBox>
        </DSBox>

        <DSBox
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: spacing(0.5),
            overflow: 'hidden',
          }}
        >
          {visibleEvents.map((event) => (
            <DSCalendarEvent
              key={event.id}
              event={event}
              onClick={() => handleCalendarEventClick(event)}
              disabledClick={isClickDisabled}
            />
          ))}

          {hiddenEventsCount > 0 && (
            <DSButton
              onClick={handleShowMore}
              sx={{
                backgroundColor: showAllEvents
                  ? palette.secondary.light
                  : KLYDO_YELLOW,
                color: showAllEvents ? KLYDO_WHITE : KLYDO_BLACK,
                borderRadius: '25px',
                fontSize: '0.7rem',
                fontFamily: 'Gilroy-Medium',
                width: '40%',
                padding: spacing(0.25, 0.75),
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: KLYDO_YELLOW,
                  opacity: 0.8,
                },
              }}
            >
              {showAllEvents ? 'Show less' : `+ ${hiddenEventsCount} more`}
            </DSButton>
          )}
        </DSBox>
      </DSBox>

      <CalendarDayDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        selectedDate={day.date}
      />
    </>
  );
};

export default DSCalendarDay;
